/**
 * 매핑·변환 제안 엔진 — 소스/타깃 항목이 이름·순서·타입·길이가 달라도 1:1 대응을 찾아 제안한다.
 * LLM 없이 도는 결정적 규칙 엔진이라 스탠드얼론(내부망)에서도 그대로 동작한다.
 *
 * 설계 원칙 (LiveSQL 설계문서 부록 A-2 실측 근거):
 *   **후보는 이름·용어로만 만든다.** 타입·길이가 같다는 이유로 후보를 만들면 조합이 폭발한다
 *   (도메인 매칭 실측 11,464건). 타입·길이·값 패턴은 이미 만들어진 후보를 **검증·경고**할 때만 쓴다.
 *
 * 변환은 선언적 op 으로 만들고, 실행용 expr 문자열은 opToExpr 이 생성한다 —
 * 화면 미리보기(TS)와 실행(파이썬 apply_mapping/_eval_expr)이 같은 근원에서 나온다.
 */
import { Column } from '../utils/transformLoad';
import { parseDataType, profileValues, typeCompatible, isDatePattern, TypeMeta, ValueProfile } from './typeMeta';
import { StdDictionary, EMPTY_DICTIONARY, normalizeName, splitByWords, splitByWordSets } from './stdDictionary';
import i18n from '../../../i18n';

const tr = (key: string, opts?: Record<string, unknown>) => String(i18n.t(`property:suggest.${key}`, opts as any));

export type Confidence = 'confirmed' | 'likely' | 'weak';
export type Evidence = 'name' | 'term' | 'word' | 'partial' | 'order' | 'ai';

/** 선언적 변환 — 미리보기(TS)와 실행식(파이썬) 양쪽을 만든다 */
export type MappingOp =
  | { kind: 'none' }
  | { kind: 'trim' }
  | { kind: 'date_format'; from: 'YYYYMMDD' | 'YYYY-MM-DD' | 'YYYY/MM/DD'; to: 'YYYYMMDD' | 'YYYY-MM-DD' }
  | { kind: 'strip_comma' }
  | { kind: 'lpad_zero'; width: number }
  | { kind: 'substr'; length: number };

export interface Suggestion {
  target: string;
  source: string;
  confidence: Confidence;
  evidence: Evidence[];
  op: MappingOp;
  /** 사용자에게 보일 경고 (길이 절단·타입 불일치 등) */
  warnings: string[];
  /** 점수 (정렬·상위 후보 선택용) */
  score: number;
}

export interface SuggestResult {
  suggestions: Suggestion[];
  /** 타깃에만 있어 채울 소스를 못 찾은 항목 — NULL 이 된다 */
  unmatchedTargets: string[];
  /** 소스에만 있어 버려지는 항목 */
  unusedSources: string[];
  dictionaryUsed: boolean;
}

export interface SuggestInput {
  sourceColumns: Column[];
  targetColumns: Column[];
  /** 소스 샘플 행 (없어도 동작한다 — 값 기반 판정만 빠진다) */
  sourceRows?: Array<Record<string, unknown>>;
  dictionary?: StdDictionary;
  /** 고정폭 SAM 의 바이트 길이 목록 등, 정의에서 온 길이 힌트 (컬럼명 → 길이) */
  targetLengthHints?: Record<string, number>;
}

interface ColMeta {
  col: Column;
  norm: string;
  type: TypeMeta;
  /** 표준용어로 찾은 논리명 (있으면 강한 신호) */
  termName?: string;
  /** 물리명을 표준단어로 분해한 것 (논리명 조립용) */
  words: string[];
  /** 조각별 동의어 집합 — 사전/약어표 어느 경로로 바뀌든 만나도록 */
  wordSets: string[][];
  profile?: ValueProfile;
}

const CONF_SCORE: Record<Confidence, number> = { confirmed: 100, likely: 60, weak: 25 };

function buildMeta(cols: Column[], dict: StdDictionary, rows?: Array<Record<string, unknown>>): ColMeta[] {
  return cols.map((col) => {
    const norm = normalizeName(col.name);
    const term = dict.byAbbr.get(norm) || dict.byName.get(norm);
    return {
      col,
      norm,
      type: parseDataType(col.dataType),
      termName: term?.termNm,
      words: splitByWords(col.name, dict),
      wordSets: splitByWordSets(col.name, dict),
      profile: rows && rows.length ? profileValues(rows.map((r) => r[col.name])) : undefined,
    };
  });
}

/** 두 항목의 이름 유사도 — 후보 생성용. 0 이면 후보 아님 */
function nameAffinity(s: ColMeta, t: ColMeta): { score: number; evidence: Evidence[] } | null {
  if (s.norm && s.norm === t.norm) return { score: 100, evidence: ['name'] };

  // 표준용어 경유 — 물리명이 달라도 같은 용어면 같은 뜻이다
  if (s.termName && t.termName && s.termName === t.termName) return { score: 95, evidence: ['term'] };
  if (s.termName && normalizeName(s.termName) === t.norm) return { score: 90, evidence: ['term'] };
  if (t.termName && normalizeName(t.termName) === s.norm) return { score: 90, evidence: ['term'] };

  // 표준단어 분해 — 조각을 이어 붙인 논리명이 같으면(CUST_NM → 고객+명 = '고객명') 같은 뜻이다
  const sJoined = normalizeName(s.words.join(''));
  const tJoined = normalizeName(t.words.join(''));
  if (sJoined && sJoined === tJoined) return { score: 88, evidence: ['word'] };
  if (sJoined && sJoined === t.norm) return { score: 86, evidence: ['word'] };
  if (tJoined && tJoined === s.norm) return { score: 86, evidence: ['word'] };

  // 조각이 겹치는 만큼 — 동의어 집합끼리 교집합이 있으면 같은 조각으로 본다
  const sw = s.wordSets.filter((x) => x.length);
  const tw = t.wordSets.filter((x) => x.length);
  if (sw.length && tw.length) {
    const usedT = new Set<number>();
    let shared = 0;
    sw.forEach((sSet) => {
      const hit = tw.findIndex((tSet, i) => !usedT.has(i) && tSet.some((w) => sSet.includes(w)));
      if (hit >= 0) { usedT.add(hit); shared += 1; }
    });
    if (shared) {
      const ratio = shared / Math.max(sw.length, tw.length);
      if (ratio >= 0.99) return { score: 85, evidence: ['word'] };
      if (ratio >= 0.5) return { score: 45 + Math.round(ratio * 20), evidence: ['word'] };
    }
  }

  // 부분 문자열 — 짧은 이름은 우연히 겹치므로 임계를 둔다
  const a = s.norm, b = t.norm;
  if (a.length >= 4 && b.length >= 4 && (a.includes(b) || b.includes(a))) {
    return { score: 40 + Math.min(a.length, b.length), evidence: ['partial'] };
  }
  return null;
}

/** 후보가 정해진 뒤 타입·길이·값으로 검증하고 변환 op 을 고른다 */
function verify(s: ColMeta, t: ColMeta, dict: StdDictionary, lenHint?: number):
  { op: MappingOp; warnings: string[]; penalty: number } {
  const warnings: string[] = [];
  let op: MappingOp = { kind: 'none' };
  let penalty = 0;

  // 타깃 기대 타입·길이 — 사전(정보유형)이 있으면 그것이 우선한다
  const tTerm = dict.byAbbr.get(t.norm) || dict.byName.get(t.norm);
  const fromInftp = tTerm?.inftpNm ? dict.inftp.get(tTerm.inftpNm) : undefined;
  const tLen = lenHint ?? t.type.length ?? fromInftp?.length;
  const tBase = t.type.base !== 'UNKNOWN' ? t.type.base : parseDataType(fromInftp?.base).base;

  // 값 패턴 → 변환
  const p = s.profile;
  if (p) {
    if (p.padded) op = { kind: 'trim' };
    if (isDatePattern(p.pattern) && p.pattern !== 'datetime') {
      const from = p.pattern.replace('date:', '') as 'YYYYMMDD' | 'YYYY-MM-DD' | 'YYYY/MM/DD';
      // 타깃이 날짜형인데 소스가 YYYYMMDD 문자열이면 구분자를 넣어 줘야 파싱된다
      if ((tBase === 'DATE' || tBase === 'TIMESTAMP') && from !== 'YYYY-MM-DD') {
        op = { kind: 'date_format', from, to: 'YYYY-MM-DD' };
      } else if (tBase === 'VARCHAR' && tLen === 8 && from !== 'YYYYMMDD') {
        op = { kind: 'date_format', from, to: 'YYYYMMDD' };
      }
    } else if (p.pattern === 'number:comma' && (tBase === 'INTEGER' || tBase === 'NUMERIC')) {
      op = { kind: 'strip_comma' };
    }
    // 길이 절단 — 샘플 최대 길이가 타깃 길이를 넘으면 실행 중 에러가 난다
    if (tLen && p.maxLen > tLen) {
      warnings.push(tr('warnSampleLen', { len: tLen, max: p.maxLen }));
      if (op.kind === 'none' || op.kind === 'trim') op = { kind: 'substr', length: tLen };
      penalty += 10;
    }
  }

  // 정의상의 길이 비교 (샘플이 없어도 잡힌다)
  if (!p && s.type.length && tLen && s.type.length > tLen) {
    warnings.push(tr('warnDefLen', { src: s.type.length, tgt: tLen }));
    penalty += 5;
  }

  const compat = typeCompatible(s.type.base, tBase);
  if (compat === 'bad') { warnings.push(tr('warnTypeBad', { src: s.type.base, tgt: tBase })); penalty += 40; }
  else if (compat === 'check' && op.kind === 'none') {
    warnings.push(tr('warnTypeCheck', { src: s.type.base, tgt: tBase }));
    penalty += 10;
  }
  return { op, warnings, penalty };
}

/**
 * 매핑 제안 — 타깃 항목마다 가장 그럴듯한 소스를 하나씩 고른다.
 * 이름으로 아무것도 못 찾았고 컬럼 수가 같을 때만 **순서 폴백**을 낮은 신뢰도로 제안한다.
 */
export function suggestMapping(input: SuggestInput): SuggestResult {
  const dict = input.dictionary || EMPTY_DICTIONARY;
  const sources = buildMeta(input.sourceColumns, dict, input.sourceRows);
  const targets = buildMeta(input.targetColumns, dict);

  // 1) 후보 생성 — 이름·용어만 본다
  interface Cand { si: number; ti: number; score: number; evidence: Evidence[] }
  const cands: Cand[] = [];
  targets.forEach((t, ti) => {
    sources.forEach((s, si) => {
      const aff = nameAffinity(s, t);
      if (aff) cands.push({ si, ti, score: aff.score, evidence: aff.evidence });
    });
  });

  // 2) 점수 높은 것부터 1:1 로 확정 (한 소스가 여러 타깃에 붙지 않게)
  cands.sort((a, b) => b.score - a.score);
  const usedSrc = new Set<number>();
  const takenTgt = new Map<number, Cand>();
  cands.forEach((c) => {
    if (usedSrc.has(c.si) || takenTgt.has(c.ti)) return;
    usedSrc.add(c.si);
    takenTgt.set(c.ti, c);
  });

  // 3) 순서 폴백 — 이름으로 하나도 못 찾았고 컬럼 수가 같을 때만
  const noneMatched = takenTgt.size === 0;
  const sameCount = sources.length === targets.length && sources.length > 0;
  if (noneMatched && sameCount) {
    targets.forEach((_t, ti) => takenTgt.set(ti, { si: ti, ti, score: 20, evidence: ['order'] }));
  }

  const suggestions: Suggestion[] = [];
  takenTgt.forEach((c, ti) => {
    const s = sources[c.si];
    const t = targets[ti];
    const lenHint = input.targetLengthHints?.[t.col.name];
    const v = verify(s, t, dict, lenHint);
    const score = Math.max(0, c.score - v.penalty);
    const confidence: Confidence =
      c.evidence.includes('order') ? 'weak'
        : score >= 85 && v.warnings.length === 0 ? 'confirmed'
          : score >= 45 ? 'likely' : 'weak';
    suggestions.push({
      target: t.col.name, source: s.col.name, confidence, evidence: c.evidence,
      op: v.op, warnings: v.warnings, score: score || CONF_SCORE[confidence],
    });
  });

  // 타깃 정의 순서대로 보여 준다 (사용자가 화면에서 읽는 순서)
  const order = new Map(input.targetColumns.map((c, i) => [c.name, i]));
  suggestions.sort((a, b) => (order.get(a.target) ?? 0) - (order.get(b.target) ?? 0));

  const matchedSources = new Set(suggestions.map((x) => x.source));
  return {
    suggestions,
    unmatchedTargets: input.targetColumns.map((c) => c.name).filter((n) => !suggestions.some((s) => s.target === n)),
    unusedSources: input.sourceColumns.map((c) => c.name).filter((n) => !matchedSources.has(n)),
    dictionaryUsed: dict.loaded,
  };
}

/** LLM 이 돌려준 짝 하나 — 서비스의 suggest_mapping 작업 출력과 같은 모양이다 */
export interface AiPair {
  target: string;
  source: string;
  confidence?: 'likely' | 'weak';
  reason?: string;
  op?: { kind: string; from?: string; to?: string; width?: number; length?: number };
}

/**
 * 규칙 엔진 결과에 LLM 제안을 **덧붙인다**. 규칙이 이미 붙인 짝은 건드리지 않는다.
 *
 * 안전 규칙 두 가지:
 *  1) 변환식은 LLM 문자열을 그대로 쓰지 않는다. 검증(verify)이 스스로 고른 op 이 우선이고,
 *     LLM 의 op 은 검증이 'none' 일 때만, 그것도 아는 종류일 때만 받는다.
 *     → 실행식(opToExpr)이 만드는 파이썬 코드는 언제나 우리가 아는 형태다.
 *  2) 타깃·소스 이름은 실제 컬럼 목록에 있는 것만 받는다(없는 이름을 지어내도 걸러진다).
 */
export function mergeAiPairs(input: SuggestInput, base: SuggestResult, pairs: AiPair[]): SuggestResult {
  const dict = input.dictionary || EMPTY_DICTIONARY;
  const sources = buildMeta(input.sourceColumns, dict, input.sourceRows);
  const targets = buildMeta(input.targetColumns, dict);
  const openTargets = new Set(base.unmatchedTargets);
  const freeSources = new Set(base.unusedSources);

  const added: Suggestion[] = [];
  for (const p of pairs || []) {
    const t = targets.find((x) => x.col.name === p.target);
    const s = sources.find((x) => x.col.name === p.source);
    if (!t || !s) continue;                       // 없는 이름 — 버린다
    if (!openTargets.has(t.col.name) || !freeSources.has(s.col.name)) continue;  // 이미 쓰인 자리
    openTargets.delete(t.col.name);
    freeSources.delete(s.col.name);

    const v = verify(s, t, dict, input.targetLengthHints?.[t.col.name]);
    const op = v.op.kind === 'none' ? (coerceOp(p.op) || v.op) : v.op;
    const warnings = [...v.warnings];
    if (p.reason) warnings.unshift(tr('aiReason', { reason: p.reason }));
    added.push({
      target: t.col.name, source: s.col.name,
      confidence: p.confidence === 'weak' ? 'weak' : 'likely',
      evidence: ['ai'], op, warnings,
      score: Math.max(0, (p.confidence === 'weak' ? 40 : 60) - v.penalty),
    });
  }
  if (!added.length) return base;

  const suggestions = [...base.suggestions, ...added];
  const order = new Map(input.targetColumns.map((c, i) => [c.name, i]));
  suggestions.sort((a, b) => (order.get(a.target) ?? 0) - (order.get(b.target) ?? 0));
  return {
    ...base,
    suggestions,
    unmatchedTargets: Array.from(openTargets),
    unusedSources: Array.from(freeSources),
  };
}

/** LLM 이 준 op 을 우리가 아는 형태로만 받아들인다 (모르는 것은 버린다) */
function coerceOp(op: AiPair['op']): MappingOp | null {
  if (!op || !op.kind) return null;
  switch (op.kind) {
    case 'trim': return { kind: 'trim' };
    case 'strip_comma': return { kind: 'strip_comma' };
    case 'lpad_zero': return op.width && op.width > 0 ? { kind: 'lpad_zero', width: Math.floor(op.width) } : null;
    case 'substr': return op.length && op.length > 0 ? { kind: 'substr', length: Math.floor(op.length) } : null;
    case 'date_format': {
      const from = op.from as any, to = op.to as any;
      const froms = ['YYYYMMDD', 'YYYY-MM-DD', 'YYYY/MM/DD'];
      const tos = ['YYYYMMDD', 'YYYY-MM-DD'];
      return froms.includes(from) && tos.includes(to) ? { kind: 'date_format', from, to } : null;
    }
    default: return null;
  }
}

// ---------------------------------------------------------------- op → 실행식 / 미리보기

/**
 * 파이썬 실행식 — backend task_functions._eval_expr 가 평가한다.
 * 행은 `row` 로 들어오고, 안전한 내장만 쓴다 (문자열 슬라이싱·replace·strip).
 */
export function opToExpr(op: MappingOp, source: string): string {
  const src = `row.get(${JSON.stringify(source)})`;
  const asStr = `str(${src} or '')`;
  switch (op.kind) {
    case 'none': return '';                       // 변환 없음 — mapping 의 source 만 쓴다
    case 'trim': return `${asStr}.strip()`;
    case 'strip_comma': return `${asStr}.replace(',', '')`;
    case 'lpad_zero': return `${asStr}.strip().rjust(${op.width}, '0')`;
    case 'substr': return `${asStr}[:${op.length}]`;
    case 'date_format': {
      const s = `${asStr}.strip()`;
      // 자리만 옮기는 변환이라 datetime 없이 슬라이싱으로 처리한다
      if (op.from === 'YYYYMMDD' && op.to === 'YYYY-MM-DD') return `(${s}[:4] + '-' + ${s}[4:6] + '-' + ${s}[6:8])`;
      if (op.from === 'YYYY/MM/DD' && op.to === 'YYYY-MM-DD') return `${s}.replace('/', '-')`;
      if (op.from === 'YYYY-MM-DD' && op.to === 'YYYYMMDD') return `${s}.replace('-', '')`;
      if (op.from === 'YYYY/MM/DD' && op.to === 'YYYYMMDD') return `${s}.replace('/', '')`;
      return '';
    }
    default: return '';
  }
}

/** 화면 미리보기 — opToExpr 과 같은 규칙을 TS 로 (실행 결과와 어긋나지 않게 한 곳에서 관리) */
export function applyOp(op: MappingOp, value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  switch (op.kind) {
    case 'none': return s;
    case 'trim': return s.trim();
    case 'strip_comma': return s.trim().replace(/,/g, '');
    case 'lpad_zero': return s.trim().padStart(op.width, '0');
    case 'substr': return s.slice(0, op.length);
    case 'date_format': {
      const t = s.trim();
      if (op.from === 'YYYYMMDD' && op.to === 'YYYY-MM-DD') return `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`;
      if (op.from === 'YYYY/MM/DD' && op.to === 'YYYY-MM-DD') return t.replace(/\//g, '-');
      if (op.from === 'YYYY-MM-DD' && op.to === 'YYYYMMDD') return t.replace(/-/g, '');
      if (op.from === 'YYYY/MM/DD' && op.to === 'YYYYMMDD') return t.replace(/\//g, '');
      return t;
    }
    default: return s;
  }
}

export function describeOp(op: MappingOp): string {
  switch (op.kind) {
    case 'none': return tr('op.none');
    case 'trim': return tr('op.trim');
    case 'strip_comma': return tr('op.strip_comma');
    case 'lpad_zero': return tr('op.lpad_zero', { width: op.width });
    case 'substr': return tr('op.substr', { length: op.length });
    case 'date_format': return tr('op.date_format', { from: op.from, to: op.to });
    default: return '';
  }
}

/** 근거·확신도 표시 이름 — 렌더할 때 부른다 */
export const evidenceLabel = (e: Evidence) => tr(`evidence.${e}`);
export const confidenceLabel = (c: Confidence) => tr(`confidence.${c}`);
